import urlcat from 'urlcat';
import type { AxiosRequestConfig } from 'axios';

import type {
    ClassicBookedLine,
    DashboardReportFields,
    DatasourceCreatePayload,
    DatasourceDetail,
    DatasourceDetectColumnsResponse,
    DatasourceFieldsResponse,
    DatasourceListItem,
    DatasourceSyncRun,
    DatasourceUpdatePayload,
    PCAAnalyticsReportRequestPayload,
    PCAAnalyticsReportResponse,
    PCACampaignFramesRequestPayload,
    PCAFrameDetails,
    PCAMapReportRequestPayload,
    PCAMapReportResponse,
    PCAReportByFieldDailyResponse,
    PCAReportByFieldHourlyResponse,
    PCAReportByFieldResponse,
    PCAReportDailyResponse,
    PCAReportEnergyByFieldResponse,
    PCAReportHourlyResponse,
    PCAReportRequestPayload,
} from '../Types/Reports';
import { PCAReportCountField, PCAReportEnvironment } from '../Types/Reports';
import { generateArrayQSParams } from '../Lib';
import type {
    LineItemUnderInvestigation,
    ModuleConstructorProps,
    TalonOrderlineError,
} from '../Types';

// Graph types
type GraphType = 'bar' | 'line' | 'pie';

// Aggregation types
type AggregationType = 'sum' | 'avg' | 'count' | 'distinct' | 'min' | 'med' | 'max';

// Granularity types
type GranularityType = 'hour' | 'day' | 'week' | 'biweek' | 'month' | 'quarter' | 'year';

// Filter operators
type FilterOperator =
    | 'between'
    | 'eq'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'neq'
    | 'like'
    | 'not_like'
    | 'in'
    | 'not_in';

// Sort direction
type SortDirection = 'asc' | 'desc';

// Available field names
type FieldName =
    | 'agency'
    | 'audience'
    | 'audience_received'
    | 'audience_received_percent'
    | 'audience_sov'
    | 'buyer'
    | 'campaign_id'
    | 'carbon_emission_kgs_co2e'
    | 'client'
    | 'creative_id'
    | 'creative_name'
    | 'day'
    | 'display_conurbation'
    | 'display_env'
    | 'display_filetypes'
    | 'display_format'
    | 'display_id'
    | 'display_lat'
    | 'display_line_item_id'
    | 'display_line_item_name'
    | 'display_long'
    | 'display_owner'
    | 'display_owner_id'
    | 'display_resolution'
    | 'display_screen_name_summary'
    | 'display_slot_duration'
    | 'display_slots'
    | 'display_sov'
    | 'display_surface_area'
    | 'display_town'
    | 'display_tv_region'
    | 'display_type'
    | 'downtime'
    | 'energy_uses_kwh'
    | 'environment'
    | 'expected_audience'
    | 'expected_impacts_in_schedule'
    | 'expected_plays'
    | 'expected_plays_in_schedule'
    | 'expected_time'
    | 'expected_time_in_schedule'
    | 'frame_id'
    | 'hour'
    | 'id'
    | 'impacts_received_in_schedule'
    | 'impacts_received_out_of_schedule'
    | 'impacts_received_unbooked'
    | 'impacts_with_verification'
    | 'inside_campaign_settings_date'
    | 'issue_id'
    | 'name'
    | 'playout_day_of_week'
    | 'playout_end_date'
    | 'playout_end_datetime'
    | 'playout_end_hour'
    | 'playout_end_time'
    | 'playout_end_timestamp'
    | 'playout_percentage_plays'
    | 'playout_percentage_time'
    | 'playout_start_date'
    | 'playout_start_datetime'
    | 'playout_start_hour'
    | 'playout_start_time'
    | 'playout_start_timestamp'
    | 'plays_received_in_schedule'
    | 'plays_received_out_of_schedule'
    | 'plays_received_unbooked'
    | 'plays_with_verification'
    | 'reference'
    | 'scheduled'
    | 'slot_duration'
    | 'sov'
    | 'sum_of_creatives'
    | 'sum_of_play'
    | 'sum_of_time'
    | 'time_received_in_schedule'
    | 'time_received_out_of_schedule'
    | 'time_received_unbooked'
    | 'time_with_verification'
    | 'unit_id';

// Metric interface
interface Metric {
    field: FieldName;
    aggregation: AggregationType;
}

// Dimension interface
interface Dimension {
    field: FieldName;
    granularity?: GranularityType | null;
}

// Filter interface
interface Filter {
    field: FieldName;
    operator: FilterOperator;
    value: any; // Laravel 'required' doesn't specify type, so using any
}

// Sort interface
interface Sort {
    field: FieldName;
    direction: SortDirection;
}

// Main request interface
interface ChartRequest {
    graphType: GraphType;
    metrics: Metric[];
    dimensions?: Dimension[];
    filters?: Filter[];
    limit?: number;
    sort?: Sort[];
}

type MediaOwnerPerformance = {
    id: string;
    name: string;
    sum_of_play: number;
    expected_plays: number;
    plays_performance: number;
    sum_of_time: number;
    expected_time: number;
    time_performance: number;
    audience_received: number;
    expected_audience: number;
    audience_performance: number;
};

type CampaignNotices = {
    overall: {
        awaiting_data: boolean;
        inspecting_data: boolean;
        awaiting_booking_plan: boolean;
        inflight_report_sent: boolean;
        pca_report_sent: boolean;
        orderline_errors: boolean;
    };
    media_owners: any[]; // adjust type if known
    line_items: any[]; // adjust type if known
};

type CampaignPerformance = {
    id: number;
    name: string;
    date_start: string;
    date_end: string;
    days_left: number;
    sum_of_play: number;
    expected_plays: number;
    sum_of_time: number;
    expected_time: number;
    audience_received: number;
    expected_audience: number;
    plays_performance: number;
    time_performance: number;
    audience_performance: number;
    booking_summary_validated: boolean;
    media_owners: Record<string, MediaOwnerPerformance>;
    notices: CampaignNotices;
    reference: string | null;
    is_talon: boolean;
    // Both empty unless `is_talon`; the same rows the Talon reporting page flags with icons.
    line_items_under_investigation: LineItemUnderInvestigation[];
    orderline_errors: TalonOrderlineError[];
};

export type CampaignPerformanceMapLiveDashboard = Record<string, CampaignPerformance>;

const Reports = ({ endpoints, httpClient, baseUrl }: ModuleConstructorProps) => ({
    pcaReport: (
        {
            campaignId,
            startDate,
            endDate,
            metric,
            campaignStats = false,
            timeFrame,
            field,
            countFields = [
                PCAReportCountField.InSchedule,
                PCAReportCountField.OutSchedule,
                PCAReportCountField.UnBooked,
            ],
            fieldFilters = {},
            environment = PCAReportEnvironment.Live,
        }: PCAReportRequestPayload,
        config?: AxiosRequestConfig,
    ) => {
        const data = {
            id: campaignId,
            start_date: startDate,
            end_date: endDate,
            metric,
            environment,
            report: `${field ?? ''}${
                timeFrame != null
                    ? field == null
                        ? String(timeFrame).toLowerCase()
                        : timeFrame
                    : ''
            }`,
        };

        if (campaignStats) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            data.campaignStats = true;
        }

        return httpClient.post<
            | PCAReportHourlyResponse
            | PCAReportDailyResponse
            | PCAReportByFieldResponse
            | PCAReportByFieldDailyResponse
            | PCAReportByFieldHourlyResponse
        >(
            `${urlcat(endpoints.reports.pca.endpoint, data)}${generateArrayQSParams(
                countFields,
                'count_fields',
                '&',
            )}`,
            fieldFilters,
            config,
        );
    },
    pcaMapReport: (
        {
            campaignId,
            startDate,
            endDate,
            metric,
            campaignStats = false,
            countFields = [
                PCAReportCountField.InSchedule,
                PCAReportCountField.OutSchedule,
                PCAReportCountField.UnBooked,
            ],
            fieldFilters = {},
            environment = PCAReportEnvironment.Live,
        }: PCAMapReportRequestPayload,
        config?: AxiosRequestConfig,
    ) => {
        const data = {
            id: campaignId,
            start: startDate,
            end: endDate,
            metric,
            environment,
        };

        if (campaignStats) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            data.campaignStats = true;
        }

        return httpClient.post<PCAMapReportResponse>(
            `${urlcat(endpoints.reports.pca.mapEndpoint, data)}${generateArrayQSParams(
                countFields,
                'count_fields',
                '&',
            )}`,
            { filters: fieldFilters },
            config,
        );
    },
    pcaAnalyticsReport: (
        {
            campaignId,
            startDate,
            endDate,
            countFields = [
                PCAReportCountField.InSchedule,
                PCAReportCountField.OutSchedule,
                PCAReportCountField.UnBooked,
            ],
            fieldFilters = {},
            environment = PCAReportEnvironment.Live,
        }: PCAAnalyticsReportRequestPayload,
        config?: AxiosRequestConfig,
    ) => {
        const data = {
            id: campaignId,
            start: startDate,
            end: endDate,
            environment,
        };

        return httpClient.post<PCAAnalyticsReportResponse>(
            `${urlcat(endpoints.reports.pca.analyticsEndpoint, data)}${generateArrayQSParams(
                countFields,
                'count_fields',
                '&',
            )}`,
            { filters: fieldFilters },
            config,
        );
    },
    pcaCampaignFrames: (
        {
            campaignId,
            metric,
            fieldFilters = {},
            startDate,
            endDate,
            environment = PCAReportEnvironment.Live,
        }: PCACampaignFramesRequestPayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<PCAFrameDetails[]>(
            `${urlcat(endpoints.reports.pca.campaignFramesEndpoint, {
                id: campaignId,
                metric,
                start: startDate,
                end: endDate,
                environment,
            })}`,
            { filters: fieldFilters },
            config,
        ),
    pcaRunReport: ({ id, consumerType = 'full', environment }, config?: AxiosRequestConfig) =>
        httpClient.post(
            `${urlcat(endpoints.reports.pca.run, { id })}?consumer=${consumerType}`,
            { environment },
            config,
        ),
    pcaDeleteReport: ({ id, environment }, config?: AxiosRequestConfig) =>
        httpClient.post(`${urlcat(endpoints.reports.pca.delete, { id })}`, { environment }, config),
    calculateTargetMultiplier: ({ id, target_id }, config?: AxiosRequestConfig) =>
        httpClient.post(
            `${urlcat(endpoints.reports.pca.targetMultiplier, { id })}`,
            { target_id },
            config,
        ),
    publishReport: ({ id }, config?: AxiosRequestConfig) =>
        httpClient.post(`${urlcat(endpoints.reports.pca.publish, { id })}`, config),
    dashboard: (
        { graphType, metrics, dimensions, filters, sort, limit = 100 }: ChartRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<DashboardReportFields>(
            urlcat(endpoints.reports.dashboard.charts, {}),
            { graphType, metrics, dimensions, filters, sort, limit },
            config,
        ),
    dashboardLive: () =>
        httpClient.get<CampaignPerformanceMapLiveDashboard>(
            urlcat(endpoints.reports.dashboard.live, {}),
        ),
    // Paginated list of saved datasources, for the per-widget datasource picker.
    datasourceList: (
        params: Record<string, string | number> = {
            perPage: 200,
            orderBy: 'name',
            direction: 'asc',
        },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<{ data: DatasourceListItem[] }>(
            urlcat(endpoints.reports.datasource.list, params),
            config,
        ),
    // The synced schema for a datasource, used to populate the field pickers.
    datasourceFields: (
        { datasource }: { datasource: string | number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<DatasourceFieldsResponse>(
            urlcat(endpoints.reports.datasource.fields, { datasource }),
            config,
        ),
    // Query a datasource. Same payload shape as `dashboard` (/reporting/dynamic); the
    // response is a bare array of row objects, same as the dynamic report body.
    datasourceQuery: (
        {
            datasource,
            graphType,
            metrics,
            dimensions,
            filters,
            sort,
            limit = 100,
        }: ChartRequest & { datasource: string | number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<DashboardReportFields>(
            urlcat(endpoints.reports.datasource.query, { datasource }),
            { graphType, metrics, dimensions, filters, sort, limit },
            config,
        ),
    // ---- Datasource authoring (CRUD + sync) ----
    // Plan the SQL and return its columns without saving/building. Cheap; debounce it.
    // source_connection picks the engine that plans it: a 'mysql' query is MySQL SQL.
    datasourceDetectColumns: (
        body: { query: string; source_connection?: string; partition_columns?: string[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<DatasourceDetectColumnsResponse>(
            urlcat(endpoints.reports.datasource.detectColumns, {}),
            body,
            config,
        ),
    datasourceCreate: (body: DatasourceCreatePayload, config?: AxiosRequestConfig) =>
        httpClient.post<{ data: DatasourceDetail }>(
            urlcat(endpoints.reports.datasource.create, {}),
            body,
            config,
        ),
    datasourceRead: (
        { datasource, includeSyncs }: { datasource: string | number; includeSyncs?: boolean },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<{ data: DatasourceDetail }>(
            urlcat(endpoints.reports.datasource.detail, {
                datasource,
                ...(includeSyncs ? { 'include[]': 'syncs' } : {}),
            }),
            config,
        ),
    datasourceUpdate: (
        { datasource, ...body }: DatasourceUpdatePayload & { datasource: string | number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ data: DatasourceDetail }>(
            urlcat(endpoints.reports.datasource.detail, { datasource }),
            body,
            config,
        ),
    datasourceDelete: (
        { datasource }: { datasource: string | number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{ success: boolean }>(
            urlcat(endpoints.reports.datasource.detail, { datasource }),
            undefined,
            config,
        ),
    datasourceSync: (
        { datasource }: { datasource: string | number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ success: boolean; message: string }>(
            urlcat(endpoints.reports.datasource.sync, { datasource }),
            {},
            config,
        ),
    datasourceSyncs: (
        { datasource, limit = 25 }: { datasource: string | number; limit?: number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<{ data: DatasourceSyncRun[] }>(
            urlcat(endpoints.reports.datasource.syncs, { datasource, limit }),
            config,
        ),
    campaignDashboard: ({ groupBy }: { groupBy?: string }, config?: AxiosRequestConfig) =>
        httpClient.post<{
            data: {
                total_count: number;
                archived_count: number;
                upcoming_count: number;
                past_count: number;
                live_count: number;
                [key: string]: string | number;
            }[];
        }>(
            urlcat(endpoints.reports.dashboard.campaignReports, {}),
            groupBy ? { groupBy } : {},
            config,
        ),

    pcaReportEnergy: (
        {
            campaignId,
            startDate,
            endDate,
            campaignStats = false,
            field,
            fieldFilters = {},
            environment = PCAReportEnvironment.Live,
        }: PCAReportRequestPayload,
        config?: AxiosRequestConfig,
    ) => {
        const data = {
            id: campaignId,
            start: startDate,
            end: endDate,
            field,
            environment,
        };

        if (campaignStats) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            data.campaignStats = true;
        }

        return httpClient.post<PCAReportEnergyByFieldResponse>(
            `${urlcat(endpoints.reports.pca.energyEndpoint, data)}`,
            { filters: fieldFilters },
            config,
        );
    },
    impressionMultipliers: (
        { id, environment }: { id: string | number; environment?: string },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get(
            `${urlcat(endpoints.reports.pca.impressionMultipliers, { id, environment })}`,
            config,
        ),
    /** Every booked classic line with its booked start (Classic Postings, UL-7975). */
    classicBookedLines: ({ id }: { id: string | number }, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: ClassicBookedLine[] }>(
            urlcat(endpoints.reports.pca.classicBookedLines, { id }),
            config,
        ),
    geopathImpressionMultipliers: (
        { id, environment }: { id: string | number; environment?: string },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get(
            `${urlcat(endpoints.reports.pca.geopathImpressionMultipliers, { id, environment })}`,
            config,
        ),
});

const generateFieldFilterQueryString = (
    fieldFilters: PCAReportRequestPayload['fieldFilters'] = {},
    firstAsQuestionMark = false,
) =>
    Object.keys(fieldFilters)
        .reduce((acc, curr, index) => {
            acc.push(
                generateArrayQSParams(
                    fieldFilters[curr],
                    `filters.${curr}`,
                    index === 0 && firstAsQuestionMark ? '?' : '&',
                ),
            );
            return acc;
        }, [] as string[])
        .join('');

export default Reports;
